import { createContext, useContext, useEffect, useReducer } from 'react';

import { fuseSearch } from '../../hooks/useFuseSearch';

import type { AIProviderProps, ActivitySearchResultItem, SimplifiedActivity } from './types';

export const MIN_AI_SEARCH_QUERY_LENGTH = 2; // Minimum length required for search queries

const actions = {
  activitiesUpdated: (activities: SimplifiedActivity[]) =>
    ({ type: 'UPDATE_ACTIVITIES', payload: activities }) as const,
  selectionUpdated: (ids: string[]) => ({ type: 'SELECTION_UPDATED', payload: ids }) as const,
  registrySuggestionsUpdated: (ids: string[]) => ({ type: 'REGISTRY_SUGGESTIONS_UPDATED', payload: ids }) as const,
  activitySelectedInVertical: (id: string) => ({ type: 'ACTIVITY_SELECTED_IN_VERTICAL', payload: id }) as const,
  activityDeselectedInVertical: (id: string) => ({ type: 'ACTIVITY_DESELECTED_IN_VERTICAL', payload: id }) as const,
  activitySelectedInSearch: (id: string) => ({ type: 'ACTIVITY_SELECTED_IN_SEARCH', payload: id }) as const,
  aiActivitySelected: (id: string) => ({ type: 'AI_ACTIVITY_SELECTED', payload: id }) as const,
  aiActivityDeselected: (id: string) => ({ type: 'AI_ACTIVITY_DESELECTED', payload: id }) as const,
  searchFieldFocused: () => ({ type: 'SEARCH_FIELD_FOCUSED' }) as const,
  searchFieldBlurred: () => ({ type: 'SEARCH_FIELD_BLURRED' }) as const,
  searchStringChanged: (searchString: string) => ({ type: 'SEARCH_STRING_CHANGED', payload: searchString }) as const,
  aiSearchStringChanged: (searchString: string) =>
    ({ type: 'AI_SEARCH_STRING_CHANGED', payload: searchString }) as const,
  aiSearchStringSubmitted: (searchString: string) =>
    ({ type: 'AI_SEARCH_STRING_SUBMITTED', payload: searchString }) as const,
  pageSubmit: () => ({ type: 'PAGE_SUBMIT' }) as const,
  aiSearchTryAgain: () => ({ type: 'AI_SEARCH_TRY_AGAIN' }) as const,
  verticalSelected: (vertical: string) => ({ type: 'VERTICAL_SELECTED', payload: vertical }) as const,
  verticalClosed: () => ({ type: 'VERTICAL_CLOSED' }) as const,
  detailsOpened: (activityId: string) => ({ type: 'DETAILS_OPENED', payload: activityId }) as const,
  detailsClosed: () => ({ type: 'DETAILS_CLOSED' }) as const,
  aiSuggestionsUpdated: (activities: ActivitySearchResultItem[]) =>
    ({ type: 'AI_SUGGESTIONS_UPDATED', payload: activities }) as const,
};

type UIActivity = SimplifiedActivity & {
  isChecked: boolean;
};

type UIVertical = {
  id: string;
  heading: string;
  activityIds: string[];
};

type State = {
  activityById: Record<string, UIActivity>;
  allActivityIds: string[];
  error?: string;
  verticalById: Record<string, UIVertical>;
  activityIdsByVerticalId: Record<string, string[]>;
  selected: string[];
  registrySuggested: string[];
  hasBeenSelected: string[];
  searched: string[];
  searchString: string;
  mode: 'search' | 'vertical' | 'ai';
  hasNoMatch: boolean;
  verticalModalId: string | undefined;
  detailsActivityId: string | undefined;
  aiSuggested: ActivitySearchResultItem[];
};

const initialState: State = {
  activityById: {},
  allActivityIds: [],
  verticalById: {},
  activityIdsByVerticalId: {},
  selected: [],
  registrySuggested: [],
  hasBeenSelected: [],
  searched: [],
  searchString: '',
  mode: 'vertical',
  hasNoMatch: false,
  verticalModalId: undefined,
  detailsActivityId: undefined,
  aiSuggested: [],
};

const filterBySearchString = (activities: UIActivity[], searchString: string, selected: string[]) => {
  const filteredActivities = activities.filter((activity) => !activity.toBeDeleted);

  return fuseSearch(
    {
      query: searchString,
      list: filteredActivities,
      getKeys: () =>
        searchString.length < 3
          ? ['title' as const]
          : [
              { name: 'subtitle' as const, weight: 1 },
              { name: 'title' as const, weight: 2 },
              { name: 'description' as const, weight: 4 },
              { name: 'keywords' as const, weight: 4 },
            ],
      optionsLimit: 10,
    },
    { ignoreLocation: true },
  )
    .map(({ item }) => item.insifyActivityCode)
    .filter((id) => !selected.includes(id));
};

const processActivities = (activities: SimplifiedActivity[]) => {
  const sortedActivities = activities.sort((a, b) => a.title.localeCompare(b.title));

  return sortedActivities.reduce<
    Pick<State, 'activityById' | 'allActivityIds' | 'verticalById' | 'activityIdsByVerticalId'>
  >(
    (state, activity) => {
      const { activityById, allActivityIds, verticalById, activityIdsByVerticalId } = state;

      const uiActivity = {
        ...activity,
        inclusions: activity.inclusions?.sort((a, b) => a.orderFunnel - b.orderFunnel),
        exclusions: activity.exclusions,
        isChecked: false,
      };

      activity.uiVerticals?.forEach((uiVertical) => {
        if (verticalById[uiVertical] === undefined) {
          verticalById[uiVertical] = {
            id: uiVertical,
            heading: uiVertical,
            activityIds: [],
          };

          activityIdsByVerticalId[uiVertical] = [];
        }

        verticalById[uiVertical].activityIds.push(activity.insifyActivityCode);

        activityIdsByVerticalId[uiVertical].push(activity.insifyActivityCode);
      });

      return {
        activityById: { ...activityById, [activity.insifyActivityCode]: uiActivity },
        allActivityIds: [...allActivityIds, activity.insifyActivityCode],
        verticalById,
        activityIdsByVerticalId,
      };
    },
    { activityById: {}, allActivityIds: [], verticalById: {}, activityIdsByVerticalId: {} },
  );
};

type Action = ReturnType<(typeof actions)[keyof typeof actions]>;

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'UPDATE_ACTIVITIES': {
      const processedActivities = processActivities(action.payload);
      state.selected.forEach((id) => {
        processedActivities.activityById[id].isChecked = true;
      });
      const hasBeenSelected = [...state.selected];

      return {
        ...state,
        ...processedActivities,
        hasBeenSelected,
      };
    }
    case 'SELECTION_UPDATED': {
      if (state.allActivityIds.length === 0) {
        return {
          ...state,
          selected: action.payload,
          hasBeenSelected: [],
        };
      }

      const activityById = { ...state.activityById };
      state.selected.forEach((id) => {
        activityById[id] = { ...activityById[id], isChecked: false };
      });
      action.payload.forEach((id) => {
        activityById[id] = { ...activityById[id], isChecked: true };
      });

      const hasBeenSelected = [
        ...state.hasBeenSelected,
        ...action.payload.filter((id) => !state.hasBeenSelected.includes(id)),
      ];

      return {
        ...state,
        activityById,
        selected: action.payload,
        hasBeenSelected,
      };
    }
    case 'REGISTRY_SUGGESTIONS_UPDATED': {
      return {
        ...state,
        registrySuggested: action.payload,
      };
    }
    case 'ACTIVITY_SELECTED_IN_VERTICAL': {
      const hasBeenSelected = state.hasBeenSelected.includes(action.payload)
        ? state.hasBeenSelected
        : [...state.hasBeenSelected, action.payload];
      return {
        ...state,
        activityById: {
          ...state.activityById,
          [action.payload]: { ...state.activityById[action.payload], isChecked: true },
        },
        selected: [...state.selected, action.payload],
        hasBeenSelected,
      };
    }
    case 'ACTIVITY_SELECTED_IN_SEARCH': {
      const hasBeenSelected = state.hasBeenSelected.includes(action.payload)
        ? state.hasBeenSelected
        : [...state.hasBeenSelected, action.payload];
      return {
        ...state,
        activityById: {
          ...state.activityById,
          [action.payload]: { ...state.activityById[action.payload], isChecked: true },
        },
        selected: [...state.selected, action.payload],
        hasBeenSelected,
        searched: [],
        searchString: '',
        mode: 'vertical',
      };
    }
    case 'AI_ACTIVITY_SELECTED': {
      const hasBeenSelected = state.hasBeenSelected.includes(action.payload)
        ? state.hasBeenSelected
        : [...state.hasBeenSelected, action.payload];
      const aiSuggested = state.aiSuggested.filter((id) => id.insifyActivityCode !== action.payload);

      return {
        ...state,
        activityById: {
          ...state.activityById,
          [action.payload]: { ...state.activityById[action.payload], isChecked: true },
        },
        selected: [...state.selected, action.payload],
        hasBeenSelected,
        aiSuggested,
      };
    }
    case 'AI_ACTIVITY_DESELECTED': {
      const selected = state.selected.filter((id) => id !== action.payload);
      const hasBeenSelected = state.hasBeenSelected.filter((id) => id !== action.payload);

      return {
        ...state,
        activityById: {
          ...state.activityById,
          [action.payload]: { ...state.activityById[action.payload], isChecked: false },
        },
        selected,
        hasBeenSelected,
      };
    }
    case 'ACTIVITY_DESELECTED_IN_VERTICAL': {
      const selected = state.selected.filter((id) => id !== action.payload);

      return {
        ...state,
        activityById: {
          ...state.activityById,
          [action.payload]: { ...state.activityById[action.payload], isChecked: false },
        },
        selected,
      };
    }
    case 'SEARCH_FIELD_FOCUSED': {
      return {
        ...state,
      };
    }
    case 'SEARCH_FIELD_BLURRED': {
      return {
        ...state,
      };
    }
    case 'SEARCH_STRING_CHANGED': {
      const mode = action.payload === '' ? 'vertical' : 'search';
      const searched =
        action.payload === ''
          ? []
          : filterBySearchString(Object.values(state.activityById), action.payload, state.selected);
      return {
        ...state,
        searchString: action.payload,
        hasNoMatch: action.payload !== '' && searched.length === 0,
        mode,
        searched,
      };
    }
    case 'AI_SEARCH_STRING_CHANGED': {
      return {
        ...state,
        searchString: action.payload,
      };
    }
    case 'AI_SEARCH_STRING_SUBMITTED': {
      return {
        ...state,
        searchString: action.payload,
      };
    }
    case 'AI_SEARCH_TRY_AGAIN': {
      return {
        ...state,
        searchString: '',
        aiSuggested: [],
      };
    }
    case 'VERTICAL_SELECTED': {
      return {
        ...state,
        verticalModalId: action.payload,
      };
    }
    case 'VERTICAL_CLOSED': {
      return {
        ...state,
        verticalModalId: undefined,
      };
    }
    case 'DETAILS_OPENED':
      return {
        ...state,
        detailsActivityId: action.payload,
      };
    case 'DETAILS_CLOSED':
      return {
        ...state,
        detailsActivityId: undefined,
      };
    case 'AI_SUGGESTIONS_UPDATED': {
      return {
        ...state,
        aiSuggested: action.payload,
      };
    }
    default:
      return state;
  }
};

const activityContext = createContext<
  | {
      state: React.ReducerState<React.Reducer<State, Action>>;
      dispatch: React.Dispatch<React.ReducerAction<React.Reducer<State, Action>>>;
    }
  | undefined
>(undefined);

type ActivityProviderProps = {
  children: React.ReactNode;
  error?: string;
  onAddActivity: (id: string) => void;
  onRemoveActivity: (id: string) => void;
  onSearchActivity: (searchString: string) => void;
  onSelectionChanged?: (selected: string[]) => void;
};

const ActivityProvider = ({
  children,
  onAddActivity,
  onRemoveActivity,
  onSearchActivity,
  onSelectionChanged = () => {},
  ...props
}: ActivityProviderProps) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
  });

  const dispatchWithSideEffects = (action: Action) => {
    dispatch(action);

    switch (action.type) {
      case 'ACTIVITY_SELECTED_IN_VERTICAL':
      case 'ACTIVITY_SELECTED_IN_SEARCH':
        onAddActivity(action.payload);
        break;
      case 'ACTIVITY_DESELECTED_IN_VERTICAL':
        onRemoveActivity(action.payload);
        break;
      case 'SEARCH_STRING_CHANGED':
        onSearchActivity(action.payload);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    onSelectionChanged(state.selected);
  }, [state.selected]);

  return (
    <activityContext.Provider value={{ state: { error: props.error, ...state }, dispatch: dispatchWithSideEffects }}>
      {children}
    </activityContext.Provider>
  );
};

type AIActivityProviderProps = {
  children: React.ReactNode;
} & AIProviderProps;

const AIActivityProvider = ({
  children,
  onAddActivity,
  onRemoveActivity,
  onSearchActivity,
  onSearchInputBlur = () => {},
  onSearchStringChanged = () => {},
  onSelectionChanged = () => {},
  onSubmit,
  ...props
}: AIActivityProviderProps) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    mode: 'ai',
  });

  const dispatchWithSideEffects = (action: Action) => {
    dispatch(action);

    switch (action.type) {
      case 'AI_ACTIVITY_SELECTED':
        onAddActivity(action.payload);
        break;
      case 'AI_ACTIVITY_DESELECTED':
        onRemoveActivity(action.payload);
        break;
      case 'AI_SEARCH_STRING_CHANGED':
        onSearchStringChanged(action.payload);
        break;
      case 'AI_SEARCH_STRING_SUBMITTED':
        onSearchActivity(action.payload);
        break;
      case 'PAGE_SUBMIT':
        onSubmit();
        break;
      case 'SEARCH_FIELD_BLURRED':
        onSearchInputBlur();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    onSelectionChanged(state.selected);
  }, [state.selected]);

  return (
    <activityContext.Provider value={{ state: { error: props.error, ...state }, dispatch: dispatchWithSideEffects }}>
      {children}
    </activityContext.Provider>
  );
};

const useActivity = () => {
  const context = useContext(activityContext);
  if (context === undefined) {
    throw new Error('useActivityPage must be used within a UseActivityPageProvider');
  }
  return context;
};

export { actions, reducer, initialState, ActivityProvider, useActivity, AIActivityProvider };
export type { UIActivity, UIVertical, State, Action };
